import { useEffect, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "../lib/auth";
import { toast } from "sonner";
import { Users, Loader2 } from "lucide-react";

export default function Join() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const navigate = useNavigate();
  const { user, authReady } = useAuth();
  const [preview, setPreview] = useState(undefined); // undefined=loading, null=invalid
  const [joining, setJoining] = useState(false);

  useEffect(() => {
    if (!token) { setPreview(null); return; }
    api.get(`/family/invites/preview?token=${encodeURIComponent(token)}`)
      .then(({ data }) => setPreview(data))
      .catch(() => setPreview(null));
  }, [token]);

  const accept = async () => {
    setJoining(true);
    try {
      const { data } = await api.post("/family/invites/accept", { token });
      toast.success(`You've joined ${data.family?.name || "the family"}`);
      navigate("/home");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not accept invite");
      setJoining(false);
    }
  };

  const goAuth = () => {
    localStorage.setItem("pending_family_invite", token);
    navigate("/auth");
  };

  return (
    <div className="min-h-screen bg-[#F2ECE3] flex items-center justify-center p-6" data-testid="join-page">
      <div className="bg-surface border-2 border-ink w-full max-w-md p-8">
        <div className="flex items-center gap-2.5 mb-8">
          <div className="w-3 h-3 bg-ember" />
          <img src="/brand/symbol-color.svg" alt="" className="h-6 w-auto" />
          <span className="font-display font-medium text-xl tracking-tight">wandr</span>
        </div>

        {preview === undefined && (
          <div className="flex items-center gap-2 text-sand-500 font-mono text-sm py-8">
            <Loader2 size={15} className="animate-spin" /> Checking your invite…
          </div>
        )}

        {preview === null && (
          <div data-testid="join-invalid">
            <h1 className="font-display font-medium text-2xl tracking-tight mb-2">Invite not valid.</h1>
            <p className="text-sm text-sand-600 mb-6">This invite link is invalid, expired, or was revoked. Ask your family member to send a new one.</p>
            <Link to="/home" className="btn-swiss btn-primary inline-block" data-testid="join-go-home">Go to Wandr</Link>
          </div>
        )}

        {preview && (
          <div data-testid="join-card">
            <div className="flex items-center gap-2 text-ember mb-3">
              <Users size={16} />
              <span className="label-tag">Family invite</span>
            </div>
            <h1 className="font-display font-medium text-2xl tracking-tight mb-2">
              {preview.inviter_name} invited you to {preview.family_name}.
            </h1>
            <p className="text-sm text-sand-600 mb-6">
              Join to see shared trips, plan together, and track the family travel budget.
              You'll get your own private forwarding address for confirmation emails.
            </p>
            {authReady && user ? (
              <button onClick={accept} disabled={joining} className="btn-swiss btn-primary w-full disabled:opacity-50" data-testid="join-accept">
                {joining ? "Joining…" : `Join as ${user.email}`}
              </button>
            ) : (
              <button onClick={goAuth} className="btn-swiss btn-primary w-full" data-testid="join-signin">
                Sign in or create an account
              </button>
            )}
            <p className="text-[11px] font-mono text-sand-400 mt-4">Invite sent to {preview.email}</p>
          </div>
        )}
      </div>
    </div>
  );
}
